import { AsyncOptions, AsyncState, useAsync } from 'react-async';
import { updateAuthToken, useAuth } from 'lib/Auth';
import * as SessionStorage from 'lib/SessionStorage';
import { clearStashes } from 'lib/stash';
import { STORAGE_KEYS } from 'constants/general';

const API_URL = process.env.REACT_APP_API_URL;

// Token refresh state management
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

// Store request parameters for retry
type RequestParams = {
  url: string;
  method: string;
  headers: Record<string, string>;
  body?: BodyInit | null;
};

type ResponseHandler<T> = (response: Response) => Promise<T>;

const handleUnauthorizedAndRetry = async <T>(
  request: Request,
  retry: boolean,
  responseHandler: ResponseHandler<T>
): Promise<T> => {
  if (!retry) {
    handle401();
    throw new Error('Unauthorized');
  }

  if (isRefreshing) {
    return new Promise<Request>((resolve, reject) => {
      failedQueue.push({ resolve: (token) => {
        // Clone the request and update authorization header
        const retryReq = request.clone();
        retryReq.headers.set('Authorization', `Bearer ${token}`);
        resolve(retryReq);
      }, reject });
    }).then(retryRequest => fetch(retryRequest).then(responseHandler));
  }

  isRefreshing = true;

  try {
    const newToken = await refreshAccessToken();
    processQueue(null, newToken);
    isRefreshing = false;

    // Clone the request and update authorization header
    const retryReq = request.clone();
    retryReq.headers.set('Authorization', `Bearer ${newToken}`);

    const response = await fetch(retryReq);
    return responseHandler(response);
  } catch (err) {
    isRefreshing = false;
    processQueue(err, null);
    handle401();
    throw err;
  }
};

const processQueue = (error: any = null, token: string | null = null): void => {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token!);
    }
  });

  failedQueue = [];
};

export const handle401 = (): void => {
  clearStashes();
  SessionStorage.clear('auth');

  // Use window.location for reliable redirect during all app states
  window.location.href = '/';
};

export const refreshAccessToken = async (): Promise<string> => {
  try {
    const response = await window.fetch(`${API_URL}/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include', // Important: includes httpOnly cookie
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Session expired');
    }

    const data = await response.json();
    const newAccessToken = data.accessToken;

    // Update token in session storage
    const authData = await SessionStorage.load(STORAGE_KEYS.AUTH);
    if (authData) {
      await SessionStorage.save(STORAGE_KEYS.AUTH, { ...authData, token: newAccessToken });
      updateAuthToken(newAccessToken);
    }

    return newAccessToken;
  } catch (error) {
    // Refresh failed, user needs to re-login
    handle401();
    throw error;
  }
};

export const formatUrl = (path: string, params: Record<string, any> = {}): string => {
  const paramsString = Object.entries(params)
    .filter(([, value]) => value !== undefined)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join('&');

  if (paramsString.length > 0) {
    return `${path}?${paramsString}`;
  }

  return path;
};

export const send = async (
  request: Request,
  retry = true
): Promise<any> => {
  // Clone request BEFORE initial fetch to preserve body for retry
  const requestClone = request.clone();
  const response = await fetch(request);

  if (response.status === 401) {
    return handleUnauthorizedAndRetry(
      requestClone,
      retry,
      async res => {
        if (!res.ok) throw await res.json();
        return res.json();
      }
    );
  }

  const data = await response.json();

  if (!response.ok) {
    throw data;
  }

  return data;
};


export const sendReturnBlob = async (
  request: Request,
  retry = true
): Promise<Blob> => {
  // Clone request BEFORE initial fetch to preserve body for retry
  const requestClone = request.clone();
  const response = await fetch(request);

  if (response.status === 401) {
    return handleUnauthorizedAndRetry(
      requestClone,
      retry,
      async res => {
        if (!res.ok) throw await res.blob();
        return res.blob();
      }
    );
  }

  const data = await response.blob();

  if (!response.ok) {
    throw data;
  }

  return data;
};

export const del = async (path: string, token?: string): Promise<any> => {
  const headers: Record<string, string> = {};

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const request = new Request(`${API_URL}/${path}`, {
    headers,
    method: 'DELETE',
    credentials: 'include'
  });

  return send(request);
};

export const get = async (
  path: string,
  token?: string,
  params: Record<string, any> = {}
): Promise<any> => {
  const headers: Record<string, string> = {};

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const request = new Request(formatUrl(`${API_URL}/${path}`, params), {
    headers,
    method: 'GET',
    credentials: 'include'
  });

  return send(request);
};

export const getBlob = async (
  path: string,
  token?: string,
  params: Record<string, any> = {}
): Promise<any> => {
  const headers: Record<string, string> = {};

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const request = new Request(formatUrl(`${API_URL}/${path}`, params), {
    headers,
    method: 'GET',
    credentials: 'include'
  });

  return sendReturnBlob(request);
};

const sendReturnBlobWithHeaders = async (
  request: Request,
  retry = true
): Promise<{ blob: Blob; headers: Headers }> => {
  // Clone request BEFORE initial fetch to preserve body for retry
  const requestClone = request.clone();
  const response = await fetch(request);

  if (response.status === 401) {
    return handleUnauthorizedAndRetry(
      requestClone,
      retry,
      async res => {
        if (!res.ok) throw await res.blob();
        const blob = await res.blob();
        return { blob, headers: res.headers };
      }
    );
  }

  const data = await response.blob();

  if (!response.ok) {
    throw data;
  }

  return { blob: data, headers: response.headers };
};

export const getBlobWithHeaders = async (
  path: string,
  token?: string,
  params: Record<string, any> = {}
): Promise<{ blob: Blob; headers: Headers }> => {
  const headers: Record<string, string> = {};

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const request = new Request(formatUrl(`${API_URL}/${path}`, params), {
    headers,
    method: 'GET',
    credentials: 'include'
  });

  return sendReturnBlobWithHeaders(request);
};

export const post = async (
  path: string,
  token?: string,
  bodyData: Record<string, any> = {}
): Promise<any> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const request = new Request(`${API_URL}/${path}`, {
    body: JSON.stringify(bodyData),
    headers,
    method: 'POST',
    credentials: 'include'
  });

  return send(request);
};

export const postFile = async (
  path: string,
  media: File,
  token?: string,
  payload?: string
): Promise<any> => {
  const headers: Record<string, string> = {};

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const body = new FormData();
  body.append('media', media);

  if(payload){
    body.append('body', payload)
  }

  const request = new Request(`${API_URL}/${path}`, {
    body,
    headers,
    method: 'POST',
    credentials: 'include'
  });

  return send(request);
};

export const postFormData = async (
  path: string,
  bodyObject: any,
  token?: string
): Promise<any> => {
  const headers: Record<string, string> = {};

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const body = new FormData();

  Object.keys(bodyObject).forEach((key: string) => {
    if (key === 'fileCategories') {
      body.append(key, JSON.stringify(bodyObject[key]));
    } else if (key === 'imageFile') {
      bodyObject[key].forEach((test: any) => {
        body.append(key, test);
      })
    } else {
      body.append(key, bodyObject[key]);
    }
  });

  const request = new Request(`${API_URL}/${path}`, {
    body,
    headers,
    method: 'POST',
    credentials: 'include'
  });

  return send(request);
};

export const putFormData = async (
  path: string,
  bodyObject: any,
  token?: string
): Promise<any> => {
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const body = new FormData();

  Object.keys(bodyObject).forEach((key: string) => {
    if (key === 'imageFile' && bodyObject[key]) {
      bodyObject[key].forEach((f: any) => body.append(key, f));
    } else if (bodyObject[key] !== undefined && bodyObject[key] !== null) {
      body.append(key, bodyObject[key]);
    }
  });

  const request = new Request(`${API_URL}/${path}`, {
    body,
    headers,
    method: 'PUT',
    credentials: 'include'
  });

  return send(request);
};

export const put = async (
  path: string,
  token?: string,
  data?: any
): Promise<any> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const request = new Request(`${API_URL}/${path}`, {
    body: JSON.stringify(data),
    headers,
    method: 'PUT',
    credentials: 'include'
  });

  return send(request);
};

export const useAPI = <T>(options: AsyncOptions<T>): AsyncState<T> => {
  const { token, user } = useAuth();

  return useAsync({ ...options, token, user });
};